import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Users, Briefcase, Calendar, Search,
  BookOpen, Menu, Command, AlertCircle,
  Home, Settings, FileText, MessageCircle,
  MapPin, Heart, NewspaperIcon, CreditCard,
  BookCheck, BarChart2
} from 'lucide-react';

const CommandMenu = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const menuItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Users, label: 'Alumni Directory', path: '/directory' },
    { icon: Briefcase, label: 'Job Portal', path: '/jobs' },
    { icon: Calendar, label: 'Events', path: '/events' },
    { icon: BookOpen, label: 'Campaigns', path: '/campaigns' },
    { icon: User, label: 'My Profile', path: '/profile' },
    { icon: MessageCircle, label: 'Chat', path: '/chat' },
    { icon: Heart, label: 'Success Stories', path: '/success-stories' },
    { icon: BookCheck, label: 'Alumni Survey', path: '/survey' },
    { icon: BarChart2, label: 'Analytics', path: '/alumni/analytics' },
    { icon: MapPin, label: 'Alumni Map', path: '/alumni-map' },
    { icon: FileText, label: 'Feedback', path: '/feedback' },
    { icon: NewspaperIcon, label: 'Newsletters', path: '/newsletters' },
    { icon: CreditCard, label: 'Alma Card', path: '/alma-card' }
  ];

  const filteredItems = menuItems.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" 
          onClick={() => setIsOpen(false)} 
        />

        <div className="inline-block w-full max-w-2xl my-16 overflow-hidden text-left align-middle transition-all transform">
          <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200">
            <div className="flex items-center px-4 border-b border-gray-200">
              <div className="flex items-center min-w-0 flex-1 px-2">
                <Command className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search commands..."
                  className="w-full px-3 py-4 bg-transparent border-0 focus:ring-0 text-gray-900 
                    placeholder-gray-400 text-sm outline-none"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filteredItems.length > 0) {
                      navigate(filteredItems[selectedIndex].path);
                      setIsOpen(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelectedIndex((prev) => 
                        prev < filteredItems.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelectedIndex((prev) => prev > 0 ? prev - 1 : prev);
                    }
                  }}
                />
              </div>
              <kbd className="hidden sm:inline-flex items-center px-2 py-1.5 text-xs font-semibold 
                text-gray-500 bg-gray-100 rounded-md border border-gray-300">
                ESC
              </kbd>
            </div>

            <div className="max-h-96 overflow-y-auto overscroll-contain py-2">
              {filteredItems.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <AlertCircle className="mx-auto h-6 w-6 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    No results found for "{searchQuery}"
                  </p>
                </div>
              ) : (
                filteredItems.map((item, index) => (
                  <button
                    key={item.path}
                    className={`w-full text-left px-4 py-3 flex items-center space-x-3 
                      transition-colors duration-100
                      ${selectedIndex === index 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    onClick={() => {
                      navigate(item?.path);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <item.icon className={`w-5 h-5 flex-shrink-0 
                      ${selectedIndex === index ? 'text-blue-600' : 'text-gray-400'}`} 
                    />
                    <span className="flex-1 text-sm truncate">{item.label}</span>
                    {selectedIndex === index && (
                      <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs 
                        font-semibold text-gray-400 border border-gray-200 rounded">
                        ↵
                      </kbd>
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span>↑↓ to navigate</span>
                  <span>↵ to select</span>
                </div>
                <div className="flex items-center">
                  <Command className="w-3 h-3 mr-1" />
                  <span>+ K to open</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandMenu;